<template>
  <Teleport to="body">
    <div
      v-if="rendered"
      id="home-menu"
      ref="panel"
      :class="[$style['menu'], shown && $style['menu--open']]"
      role="dialog"
      aria-modal="true"
      aria-label="Menu">
      <Button variant="outline" :class="$style['menu__close']" @click="close">Sluit</Button>
      <nav aria-label="Hoofdmenu">
        <ol :class="$style['menu__list']">
          <li v-for="(item, i) in items" :key="item.to" :style="{ '--i': i }">
            <div :class="$style['menu__row']">
              <NuxtLink :to="item.to" :class="$style['menu__link']" :aria-current="route.path === item.to ? 'page' : undefined" @click="close">{{ item.name }}</NuxtLink>
              <!-- The sub pages fold away behind a plus, so the main links stay readable -->
              <button
                v-if="item.children.length"
                type="button"
                :class="[$style['menu__toggle'], expanded.includes(item.to) && $style['menu__toggle--open']]"
                :aria-expanded="expanded.includes(item.to)"
                :aria-controls="`menu-sub-${i}`"
                @click="toggle(item.to)">
                <Icon name="Plus" />
                <span :class="$style['menu__sr-only']">Onderdelen van {{ item.name }}</span>
              </button>
            </div>
            <ul v-if="item.children.length" :id="`menu-sub-${i}`" :class="[$style['menu__children'], expanded.includes(item.to) && $style['menu__children--open']]">
              <li v-for="child in item.children" :key="child.to">
                <NuxtLink :to="child.to" :class="$style['menu__child-link']" :aria-current="route.path === child.to ? 'page' : undefined" @click="close">{{ child.name }}</NuxtLink>
              </li>
            </ul>
          </li>
        </ol>
      </nav>
      <div :class="$style['menu__side']" :style="{ '--i': items.length }">
        <Button variant="primary" :to="ticketsTo" :class="$style['menu__tickets']" @click="close">Kaarten</Button>
        <div>
          <h2>De voorstellingen</h2>
          <address>Schouwburg Hengelo<br>Beursstraat 44, Hengelo</address>
        </div>
        <div>
          <h2>Hengelose Revue</h2>
          <address>Torenlaan 5-3, 7559 PK Hengelo<br>074 291 8579<br>secretariaat@hengeloserevue.nl</address>
        </div>
        <div>
          <h2>Volg de revue</h2>
          <p>
            <a href="https://www.facebook.com/hengeloserevue" target="_blank" rel="noopener" :class="$style['menu__side-link']">Facebook</a>
            <a href="https://www.instagram.com/hengeloserevue/" target="_blank" rel="noopener" :class="$style['menu__side-link']">Instagram</a>
          </p>
        </div>
      </div>
    </div>
  </Teleport>
</template>
<script setup lang="ts">
// A velvet fly curtain that drops from the top with the full navigation
const props = defineProps<{
  open: boolean
  items: Array<{ name: string, to: string, children: Array<{ name: string, to: string }> }>
  ticketsTo: string
}>()
const emit = defineEmits<{ 'update:open': [boolean] }>()
const route = useRoute()

const rendered = ref(false)
const shown = ref(false)
// Sections whose sub pages are unfolded; the section you are in starts open
const expanded = ref<Array<string>>([])
const toggle = (to: string) => {
  expanded.value = expanded.value.includes(to) ? expanded.value.filter(value => value !== to) : [...expanded.value, to]
}
const panel = ref<HTMLElement>()
let returnFocus: HTMLElement | null = null
let hideTimer: ReturnType<typeof setTimeout> | undefined

const close = () => emit('update:open', false)
const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') close() }

watch(() => props.open, async open => {
  clearTimeout(hideTimer)
  if (open) {
    returnFocus = document.activeElement as HTMLElement | null
    expanded.value = props.items.filter(item => route.path.startsWith(`${item.to}/`)).map(item => item.to)
    rendered.value = true
    document.documentElement.style.overflow = 'hidden'
    document.addEventListener('keydown', onKey)
    await nextTick()
    requestAnimationFrame(() => {
      shown.value = true
      panel.value?.querySelector<HTMLElement>('a')?.focus({ preventScroll: true })
    })
  } else {
    shown.value = false
    document.documentElement.style.overflow = ''
    document.removeEventListener('keydown', onKey)
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    hideTimer = setTimeout(() => { rendered.value = false }, reduce ? 0 : 800)
    returnFocus?.focus({ preventScroll: true })
  }
})

onBeforeUnmount(() => {
  clearTimeout(hideTimer)
  document.documentElement.style.overflow = ''
  document.removeEventListener('keydown', onKey)
})
</script>
<style module>
.menu {
  position: fixed;
  inset: 0;
  z-index: 3000;
  overflow-y: auto;
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);

  /* From the top, so the links stay put when a section unfolds */
  align-items: start;
  gap: var(--spacing-xxxl);
  padding: clamp(5.5rem, 12vh, 8rem) var(--stage-gutter) var(--spacing-xxxl);
  color: var(--stage-white);
  background:
    linear-gradient(oklch(0% 0 0deg / 55%), oklch(0% 0 0deg / 35%) 40%, oklch(0% 0 0deg / 70%)),
    repeating-linear-gradient(
      90deg,
      var(--velours-1) 0,
      var(--velours-2) 1.3rem,
      var(--velours-3) 2.4rem,
      var(--velours-2) 3.5rem,
      var(--velours-1) 4.8rem
    );
  transform: translateY(-100%);
  transition: transform 800ms var(--stage-ease);
}

.menu--open {
  transform: none;
}

.menu__close {
  position: absolute;
  top: var(--spacing-xl);
  right: var(--stage-gutter);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  box-shadow: inset 0 0 0 0.125rem var(--stage-white);
}

.menu__list {
  display: grid;
  gap: var(--spacing-m);
  max-width: 32rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.menu__list > li,
.menu__side > * {
  opacity: 0;
  transform: translateY(1.5rem);
  transition: opacity 450ms, transform 600ms var(--stage-ease);
}

.menu--open .menu__list > li,
.menu--open .menu__side > * {
  opacity: 1;
  transform: none;
  transition-delay: calc(300ms + var(--i, 0) * 55ms);
}

.menu__link {
  display: inline-block;
  font-family: var(--font-family-heading);
  font-weight: var(--font-weight-bold);
  font-size: clamp(2rem, 0.75rem + min(2.2vw, 3.6svh), 2.75rem);
  line-height: 0.98;
  letter-spacing: -0.03em;
  text-transform: uppercase;
  color: var(--stage-white);
  text-decoration: none;
  transition: color 200ms, transform 300ms var(--stage-ease);
}

.menu__link:hover,
.menu__link:focus-visible,
.menu__link[aria-current] {
  color: var(--primary-color);
  transform: translateX(0.15em);
}

.menu__children {
  display: none;
  margin: var(--spacing-l) 0 var(--spacing);
  padding: 0 0 0 var(--spacing-l);
  border-left: var(--stage-hairline) solid var(--stage-line);
  list-style: none;
  font-size: var(--font-size-base);
}

.menu__children--open {
  display: grid;
  gap: var(--spacing-m);
}

.menu__child-link {
  line-height: 1.3;
  color: var(--stage-white);
  text-decoration: none;
}

.menu__child-link:hover,
.menu__child-link[aria-current] {
  color: var(--primary-color);
}

.menu__side {
  display: grid;
  gap: var(--spacing-xl);
  align-content: center;
  font-size: var(--font-size-base);
  line-height: 1.6;
}

.menu__side h2 {
  margin: 0 0 var(--spacing);
  font-family: var(--font-family);
  font-weight: var(--font-weight-normal);
  font-size: var(--font-size-small);

  /* Its own line height: the 0.92 of the big headings glues a small label to the text under it */
  line-height: 1.4;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--stage-soft);
}

.menu__side address,
.menu__side p {
  margin: 0;
  font-size: inherit;
  font-style: normal;
}

.menu__side p {
  display: flex;
  gap: var(--spacing-l);
}

.menu__side-link {
  color: var(--stage-white);
}

.menu__tickets {
  justify-self: start;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.menu__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-l);
}

.menu__toggle {
  display: grid;
  place-items: center;
  flex: none;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border: 0.125rem solid var(--stage-line);
  border-radius: 50%;
  background: none;
  color: var(--stage-white);
  cursor: pointer;
  transition: transform 300ms var(--stage-ease), border-color 200ms;
}

.menu__toggle svg {
  width: 1.25rem;
  height: 1.25rem;
}

.menu__toggle:hover,
.menu__toggle--open {
  border-color: var(--stage-white);
}

.menu__toggle--open {
  transform: rotate(45deg);
}

.menu__sr-only {
  position: absolute;
  width: var(--stage-hairline);
  height: var(--stage-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* Phones: one column, the practical information quieter at the end */
@media (width <= 48rem) {
  .menu {
    grid-template-columns: 1fr;
    align-content: start;
    gap: var(--spacing-xxl);
  }

  .menu__list {
    gap: var(--spacing-l);
  }

  .menu__link {
    font-size: clamp(1.75rem, 1rem + 4vw, 2.25rem);
  }

  .menu__side {
    gap: var(--spacing-l);
    padding-top: var(--spacing-xl);
    border-top: var(--stage-hairline) solid var(--stage-line);
    font-size: var(--font-size-small);
    line-height: 1.5;
  }

  .menu__side address,
  .menu__side p {
    color: var(--stage-soft);
  }

  .menu__side h2 {
    margin-bottom: var(--spacing-xs);
    font-size: 0.75rem;
  }

  .menu__tickets {
    margin-bottom: var(--spacing);
  }
}

@media (prefers-reduced-motion: reduce) {
  .menu,
  .menu__list > li,
  .menu__side > *,
  .menu__link,
  .menu__toggle {
    transition: none;
  }
}
</style>
